// [목적] folio 소셜 인터랙션 도메인 zod 스키마 — 댓글(comment)/좋아요(like)/북마크(bookmark) BE/FE 공유
// [이유] folio-social-and-profile Spec WC-DTO-SOCIAL-01 — FE 폼 검증·MSW handler, BE 컨트롤러 입력 검증이
//         동일 스키마를 사용하여 타입 이중 정의를 제거함
// [사이드 이팩트] apps/api(folio-social) + apps/web(FolioPage) 양쪽 회귀 검증 필요
//               fe-be-gap-tier2 T11: folioCommentSchema.author 추가 — users LEFT JOIN 작성자 메타(ADR-T2-3)
// [수정 시 고려사항] soft-delete tombstone 스키마(isDeleted + body=null) 변경 시 FE CommentTree 컴포넌트도 동기 갱신 필요
//                   author nullable: 탈퇴/익명 시 null graceful(ADR-T2-3). folio.ts folioAuthorSchema 와 구조 동일.

import { z } from 'zod';

// ─── Comment ─────────────────────────────────────────────────────────────────

/**
 * fe-be-gap-tier2 G9: 댓글 작성자 메타 — users LEFT JOIN 으로 공급(ADR-T2-3).
 * nullable: 탈퇴/익명 시 null. folio.ts folioAuthorSchema 와 동일 구조.
 */
// festival-comments Spec T1: festival-comment.ts 가 동일 작성자 스키마를 공유하도록 export 승격.
export const commentAuthorSchema = z.object({
  handle: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
});

/**
 * DB row 직렬화 형태 — folio_comments 테이블 컬럼과 1:1 매핑.
 * soft-delete 시 body=null + isDeleted=true 로 tombstone 유지 (트리 구조 보존).
 * fe-be-gap-tier2 T11: author 필드 추가 — authorUserId 기준 users LEFT JOIN.
 */
export const folioCommentSchema = z.object({
  id: z.string().uuid(),
  portfolioId: z.string().uuid(),
  authorUserId: z.string().uuid().nullable(),
  parentId: z.string().uuid().nullable(),
  body: z.string().nullable(), // soft-delete 시 null
  hidden: z.boolean(),
  isDeleted: z.boolean(), // deletedAt != null 여부
  createdAt: z.string(),
  updatedAt: z.string(),
  /** fe-be-gap-tier2 G9: 작성자 메타. authorUserId → users LEFT JOIN. 탈퇴/익명/null 시 author=null */
  author: commentAuthorSchema.nullable().optional(),
});

/**
 * POST /folios/:id/comments 요청 body.
 * parentId 는 대댓글 시에만 지정.
 */
export const createCommentInputSchema = z.object({
  text: z.string().min(1).max(1000),
  parentId: z.string().uuid().optional(),
});

/**
 * PATCH /folios/:id/comments/:commentId 요청 body.
 */
export const patchCommentInputSchema = z.object({
  text: z.string().min(1).max(1000),
});

/**
 * GET /folios/:id/comments 쿼리 파라미터.
 * parentId filter — null 이면 최상위 댓글 전용, 지정 시 해당 부모의 대댓글.
 */
export const commentListQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  parentId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type FolioComment = z.infer<typeof folioCommentSchema>;
export type CreateCommentInput = z.infer<typeof createCommentInputSchema>;
export type PatchCommentInput = z.infer<typeof patchCommentInputSchema>;
export type CommentListQuery = z.infer<typeof commentListQuerySchema>;
export type CommentAuthor = z.infer<typeof commentAuthorSchema>;

// ─── Like ─────────────────────────────────────────────────────────────────────

/**
 * POST / DELETE /folios/:id/like 응답 envelope 내부 data 필드.
 * alreadyLiked — idempotent POST 시 중복 감지용.
 */
export const likeResponseSchema = z.object({
  liked: z.boolean(),
  likeCount: z.number().int().nonnegative(),
  alreadyLiked: z.boolean(),
});

export type LikeResponse = z.infer<typeof likeResponseSchema>;

// ─── Bookmark ─────────────────────────────────────────────────────────────────

/**
 * POST / DELETE /folios/:id/bookmark 응답 envelope 내부 data 필드.
 */
export const bookmarkResponseSchema = z.object({
  bookmarked: z.boolean(),
  bookmarkCount: z.number().int().nonnegative(),
});

export type BookmarkResponse = z.infer<typeof bookmarkResponseSchema>;
